import { css, FlattenSimpleInterpolation, SimpleInterpolation } from 'styled-components';

import { VALID_FONTS } from './Theme/common';

interface IMediaQueryOptions {
  minWidth?: number;
  ie11?: boolean;
}

type MediaQueryBuild = (options?: IMediaQueryOptions) =>
    (strings: TemplateStringsArray, ...interpolations: SimpleInterpolation[]) =>
      FlattenSimpleInterpolation;

export const buildMediaQuery: MediaQueryBuild = (options?: IMediaQueryOptions) => (literals, ...placeholders) => {
  const minWidth = `(min-width: ${options && options.minWidth || 0}em)`;
  const ie11 = options && options.ie11 ? `(-ms-high-contrast: none), (-ms-high-contrast: active)` : undefined;

  const query = [minWidth, ie11]
    .filter(param => param !== undefined) // omit from query if undefined
    .join(` and `);

  return css`
    @media ${query} {
      ${css(literals, ...placeholders)}
    }
  `;
};

export type VALID_BREAKPOINTS =
  | 'xsmall'
  | 'small'
  | 'smallMedium'
  | 'medium'
  | 'mediumLarge'
  | 'large'
  | 'xlarge'
  | 'xxlarge';

// Added 0.01 to all values to match the original Sass which uses > instead of >=
export const BreakPointsMap: { [key in VALID_BREAKPOINTS]: number } = {
  xsmall: 28.125,
  small: 37.51,
  smallMedium: 44.39,
  medium: 51.26,
  mediumLarge: 58.125,
  large: 70.01,
  xlarge: 80.01,
  xxlarge: 90.01
};

export const media = {
  xsmall: buildMediaQuery({ minWidth: BreakPointsMap.xsmall }),
  small: buildMediaQuery({ minWidth: BreakPointsMap.small }),
  smallMedium: buildMediaQuery({ minWidth: BreakPointsMap.smallMedium }),
  medium: buildMediaQuery({ minWidth: BreakPointsMap.medium }),
  mediumLarge: buildMediaQuery({ minWidth: BreakPointsMap.mediumLarge }),
  large: buildMediaQuery({ minWidth: BreakPointsMap.large }),
  xlarge: buildMediaQuery({ minWidth: BreakPointsMap.xlarge }),
  xxlarge: buildMediaQuery({ minWidth: BreakPointsMap.xxlarge }),

  ie11: buildMediaQuery({ ie11: true }),

  build: buildMediaQuery // exposes the builder so clients can build adhoc queries
};

const EFFRA_FAMILY: string = `'effra', Helvetica, Arial, 'Lucida Grande', sans-serif`;
const OPEN_SANS_FAMILY: string = `'Open Sans', Helvetica, Arial, 'Lucida Grande', sans-serif`;
const WEBLYSLEEK_FAMILY: string = `'weblysleek', Helvetica, Arial, 'Lucida Grande', sans-serif`;
const MICR_FAMILY: string = `'micr_encodingregular', Helvetica, Arial, 'Lucida Grande', sans-serif`;
const UNIVERS_NEXT_FAMILY: string = `'universnext', Helvetica, Arial, 'Lucida Grande', sans-serif`;
const PNC_SANS: string = `'pnc_sans', Helvetica, Arial, 'Lucida Grande', sans-serif`;
const SAILEC_FAMILY: string = `'sailec', Helvetica, Arial, 'Lucida Grande', sans-serif`;
const STAGBOOK: string = `'stagbook', Helvetica, Arial, 'Lucida Grande', sans-serif;`;
const PROXIMA_NOVA: string = `'proxima_nova', Helvetica, Arial, 'Lucida Grande', sans-serif;`;
const ROBOTO: string = `'roboto', Helvetica, Arial, 'Lucida Grande', sans-serif;`;
const LATO: string = `'lato', Helvetica, Arial, 'Lucida Grande', sans-serif;`;
const BENTON_SANS: string = `'benton-sans', Helvetica, Arial, 'Lucida Grande', sans-serif;`;
const OPTIMIST: string = `'optimist', Helvetica, Arial, 'Lucida Grande', sans-serif;`;
const OPTIMIST_LIGHT: string = `'optimist_light', Helvetica, Arial, 'Lucida Grande', sans-serif;`;
const OPTIMIST_XLIGHT: string = `'optimist_xlight', Helvetica, Arial, 'Lucida Grande', sans-serif;`;
const OPTIMIST_SEMIBOLD: string = `'optimist_semibold', Helvetica, Arial, 'Lucida Grande', sans-serif;`;
const OPTIMIST_BOLD: string = `'optimist_bold', Helvetica, Arial, 'Lucida Grande', sans-serif;`;
const EXPERT_SANS: string = `'expert_sans', Helvetica, Arial, 'Lucida Grande', sans-serif;`;
const EXPERT_SANS_BOLD: string = `'expert_sans_bold', Helvetica, Arial, 'Lucida Grande', sans-serif;`;
const EXPERT_SANS_SM: string = `'expert_sans_sm', Helvetica, Arial, 'Lucida Grande', sans-serif;`;
const EXPERT_SANS_SM_BOLD: string = `'expert_sans_sm_bold', Helvetica, Arial, 'Lucida Grande', sans-serif;`;
const INTER_REGULAR: string = `'inter_regular', Helvetica, Arial, 'Lucida Grande', sans-serif`;
const INTER_SEMIBOLD: string = `'inter_semibold', Helvetica, Arial, 'Lucida Grande', sans-serif`;
const INTER_BOLD: string = `'inter_bold', Helvetica, Arial, 'Lucida Grande', sans-serif`;
const INTER_LIGHT: string = `'inter_light', Helvetica, Arial, 'Lucida Grande', sans-serif`;

export const FONT_MAP: { [font in VALID_FONTS]: string } = {
  effra: EFFRA_FAMILY,
  opensans: OPEN_SANS_FAMILY,
  expert_sans: EXPERT_SANS,
  expert_sans_bold: EXPERT_SANS_BOLD,
  expert_sans_sm: EXPERT_SANS_SM,
  expert_sans_sm_bold: EXPERT_SANS_SM_BOLD,
  weblysleek: WEBLYSLEEK_FAMILY,
  micr: MICR_FAMILY,
  universnext: UNIVERS_NEXT_FAMILY,
  pnc_sans: PNC_SANS,
  sailec: SAILEC_FAMILY,
  stagbook: STAGBOOK,
  proxima_nova: PROXIMA_NOVA,
  roboto: ROBOTO,
  lato: LATO,
  benton_sans: BENTON_SANS,
  optimist: OPTIMIST,
  optimist_light: OPTIMIST_LIGHT,
  optimist_xlight: OPTIMIST_XLIGHT,
  optimist_semibold: OPTIMIST_SEMIBOLD,
  optimist_bold: OPTIMIST_BOLD,
  inter_regular: INTER_REGULAR,
  inter_semibold: INTER_SEMIBOLD,
  inter_bold: INTER_BOLD,
  inter_light: INTER_LIGHT,
};
